<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.selling_price_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                column="1"
                @click="openAddSellingPrice">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>

        <hts-data-table
            v-if="checkPermission(layoutPermissions.selling_price_management)"
            ref="dataTables"
            :headers="headers"
            :server="apiSellingPrices"
            :use-archive="useSellingPriceArchive"
            use-selection
            store-name="sellingPriceDataTable"
            store-key="SellingPrice" />

        <add-selling-price
            ref="addSellingPrice"
            @refresh-tables="refreshTables" />

        <edit-selling-price
            ref="editSellingPrice"
            @refresh-tables="refreshTables" />

        <detail-selling-price
            ref="detailSellingPrice"
            @refresh-tables="refreshTables" />

        <hts-modal
            v-if="checkPermission(layoutPermissions.selling_price_delete)"
            :show="deleteSellingPrice"
            :title="$t('labels.sellingPriceDelete')"
            type="confirm"
            :content="$t('labels.sellingPriceConfirmDelete')"
            @hide="closeDeleteSellingPrice"
            @confirm="actionDeleteSellingPrice" />

    <!-- <router-view @refresh-tables="refreshTables" /> -->
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import AddSellingPrice from './ignore/add.vue'
import { IVendorPrice } from './ignore/data'
import DetailSellingPrice from './ignore/detail.vue'
import EditSellingPrice from './ignore/edit.vue'
import Status from './ignore/status.vue'
import { checkPermission } from '@/utils/permission-check'
import Actions from '@/pages/trucker-[id]/vehicle/ignore/actions.vue'
import layoutPermissions from '@/data/permissions/layout-permissions'

const { $formatters, $lodash } = useNuxtApp()
const route = useRoute()
const dataTables = ref()
const addSellingPrice = ref()
const detailSellingPrice = ref()
const editSellingPrice = ref()
const deleteSellingPrice = ref(false)
const sellingPriceSelectedForDelete = ref<String>('')

// API endpoints
const apiSellingPrices = {
  method: 'post',
  url: 'pp/selling-price/list'
}

const useSellingPriceArchive = {
  api: {
    method: 'delete',
    url: '/pp/selling-price/archive'
  },
  permissions: checkPermission(layoutPermissions.selling_price_archive)
}

// Function to open edit selling price
const openEdit = (sellingPrice: IVendorPrice) => {
  if (sellingPrice && sellingPrice._id) {
    // useRouter().push({
    //   name: 'pp-id-selling-price-sellingPriceId-edit',
    //   params: {
    //     // id: route.params.id,
    //     sellingPriceId: sellingPrice._id.toString()
    //   }
    // })

    if (editSellingPrice?.value && sellingPrice._id) {
      editSellingPrice.value.open(sellingPrice._id)
    }
  }
}

// Function to open view detail
const openViewDetail = (sellingPrice: IVendorPrice) => {
  if (detailSellingPrice?.value && sellingPrice._id) {
    detailSellingPrice.value.open(sellingPrice._id)
  }
}

// Function to open delete confirmation
const openDelete = (sellingPrice: IVendorPrice) => {
  sellingPriceSelectedForDelete.value = sellingPrice._id || ''
  deleteSellingPrice.value = true
}

const headers = [
  {
    field: 'vendor',
    headerName: $t('labels.nameFleetVehicle'),
    minWidth: 250,
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      return params.data?.vendor?.name || ''
    }
  },
  {
    field: 'positions',
    headerName: $t('labels.route'),
    filter: 'agTextColumnFilter',
    minWidth: 500,
    valueFormatter: (params: any) => {
      if (params.data?.positions && Array.isArray(params.data?.positions)) {
        const pluckData = $lodash.map(params.data?.positions, 'fullNameNode')

        return $lodash.join(pluckData, ' >> ')
      }
    }
  },
  {
    field: 'price',
    headerName: 'Giá bán',
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      return params.data.price ? $formatters.formatCurrency(params.data.price) : ''
    }
  },
  {
    field: 'workflowName',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.workflow')
  },
  {
    field: 'typeCommodity',
    headerName: $t('labels.typeCommodity'),
    valueGetter: (params: any) => {
      const result: Array<string> = []
      if (params.data.typeCommodity && Array.isArray(params.data.typeCommodity)) {
        params.data.typeCommodity.forEach((item: string) => {
          if (item === 'NORMAL') {
            result.push($t('labels.ordinaryGoods'))
          } else if (item === 'FROZEN') {
            result.push($t('labels.frozen'))
          } else if (item === 'DANGEROUS') {
            result.push($t('labels.dangerousGoods'))
          } else if (item === 'FRAGILE') {
            result.push($t('labels.fragileGoods'))
          } else if (item === 'LIQUID') {
            result.push($t('labels.liquid'))
          } else if (item === 'GAS') {
            result.push($t('labels.gas'))
          } else if (item === 'OVERWEIGHT') {
            result.push($t('labels.overweightCargo'))
          }
        })
      }
      return result.join(',')
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: $t('labels.notActiveYet'), value: 'notActiveYet', class: { '!bg-yellow-100': true, '!text-yellow-600': true } }, // Not Active Yet: Màu vàng
      { name: $t('labels.active'), value: 'active', class: { '!bg-green-100': true, '!text-green-600': true } }, // Active: Màu xanh lá cây
      { name: $t('labels.expired'), value: 'expired', class: { '!bg-red-100': true, '!text-red-600': true } } // Expired: Màu đỏ
    ],
    filterModelDefault: { type: 'equals', filterType: 'text' }
  },
  {
    field: 'oilPrice',
    headerName: $t('labels.oilPrice'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      return params.data.oilPrice ? $formatters.formatCurrency(params.data.oilPrice) : ''
    }
  },
  {
    field: 'vehicleCategory',
    headerName: $t('labels.vehicleType'),
    valueFormatter: (params: any) => {
      if (params.data?.transportation === 'NON_CONTAINER' && params.data?.vehicleCategoryName) {
        return params.data?.vehicleCategoryName
      }
      return ''
    }
  },
  {
    field: 'contCategory',
    headerName: $t('labels.containerType'),
    valueFormatter: (params: any) => {
      if (params.data?.transportation === 'CONTAINER' && params.data?.contCategory) {
        if (params.data?.contCategory === 20) {
          return $t('labels.20f')
        }
        if (params.data?.contCategory === 40) {
          return $t('labels.40f')
        }
        if (params.data?.contCategory === 45) {
          return $t('labels.45f')
        }
      }
      return ''
    }
  },
  {
    field: 'priceOfflineFee',
    headerName: $t('labels.type'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data.priceOfflineFee) {
        return $t('labels.pricesFromOfflineFees')
      }
      return $t('labels.priceNode')
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'sellingPriceDataTable',
      storeName: 'SellingPrice',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.selling_price_edit),
          click: openEdit
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.selling_price_management),
          click: openViewDetail,
          isAll: true
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-500 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.selling_price_delete),
          click: openDelete,
          isArchive: true
        }
      ]
    }
  }
]

// Function to open add selling price modal
const openAddSellingPrice = () => {
  addSellingPrice.value && addSellingPrice.value.open()
}

// Function to close delete confirmation
const closeDeleteSellingPrice = () => {
  sellingPriceSelectedForDelete.value = ''
  deleteSellingPrice.value = false
}

// Function to delete selling price
const actionDeleteSellingPrice = $lodash.debounce(() => {
  const api = {
    method: 'delete',
    url: '/pp/selling-price'
  }

  $api(api, { body: { ids: [sellingPriceSelectedForDelete.value] } }).then((data: any) => {
    const resData: any = data.data.value
    if (resData?.success) {
      sellingPriceSelectedForDelete.value = ''
      refreshTables()
      deleteSellingPrice.value = false
      process.client && $toast().success($t('labels.deleteSuccess'))
    }
  })
}, 300)

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

defineExpose({
  refreshTables
})
</script>
